import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { ConfigurationLoaderProps, ConfigurationLoaderReturn } from '../types';

export function useConfigurationLoader({
  setColumnVisibilityModel,
  setColumnOrder,
  hasLoadedFromSavedRef,
  dispatch,
  onSuccess,
  onError,
}: ConfigurationLoaderProps): ConfigurationLoaderReturn {
  const location = useLocation();
  const navigate = useNavigate();
  const [isLoadingConfiguration, setIsLoadingConfiguration] = useState(false);

  // Use refs for callbacks so they don't trigger the effect on every render
  const setColumnVisibilityModelRef = useRef(setColumnVisibilityModel);
  setColumnVisibilityModelRef.current = setColumnVisibilityModel;
  const setColumnOrderRef = useRef(setColumnOrder);
  setColumnOrderRef.current = setColumnOrder;
  const dispatchRef = useRef(dispatch);
  dispatchRef.current = dispatch;
  const onSuccessRef = useRef(onSuccess);
  onSuccessRef.current = onSuccess;
  const onErrorRef = useRef(onError);
  onErrorRef.current = onError;
  const navigateRef = useRef(navigate);
  navigateRef.current = navigate;

  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const loadColumnsId = urlParams.get('loadColumns');
    const loadCompleteId = urlParams.get('loadComplete');

    if (!loadCompleteId && !loadColumnsId) return;

    const removeUrlParam = (paramName: string) => {
      const params = new URLSearchParams(window.location.search);
      params.delete(paramName);
      const newSearch = params.toString();
      navigateRef.current(
        { pathname: window.location.pathname, search: newSearch ? `?${newSearch}` : '' },
        { replace: true },
      );
    };

    const loadConfigurationFromDynamoDB = async (configId: string, isComplete: boolean) => {
      setIsLoadingConfiguration(true);
      dispatchRef.current({ type: 'SET_LOADING', payload: true });

      try {
        const { getFilterById } = await import('../../../../../../services/savedFiltersService');
        const configData = await getFilterById(configId);

        if (configData) {
          if (configData.columnVisibilityModel) {
            hasLoadedFromSavedRef.current = true;
            setColumnVisibilityModelRef.current(configData.columnVisibilityModel);
          }
          if (configData.columnOrder) {
            setColumnOrderRef.current(configData.columnOrder);
          }

          onSuccessRef.current(
            `${isComplete ? 'Complete search' : 'Column configuration'} "${configData.name}" applied successfully`,
          );

          removeUrlParam(isComplete ? 'loadComplete' : 'loadColumns');
        } else {
          onErrorRef.current(`${isComplete ? 'Complete search' : 'Column configuration'} not found`);
        }
      } catch (error) {
        console.error(`Error loading ${isComplete ? 'complete search' : 'column configuration'}:`, error);
        onErrorRef.current(`Error loading ${isComplete ? 'complete search' : 'column configuration'}`);
      } finally {
        setIsLoadingConfiguration(false);
        dispatchRef.current({ type: 'SET_LOADING', payload: false });
      }
    };

    const loadFromLocalStorage = (key: string, type: 'complete' | 'columns') => {
      try {
        const data = localStorage.getItem(key);
        if (data) {
          const parsed = JSON.parse(data);

          if (parsed.columnVisibilityModel) {
            hasLoadedFromSavedRef.current = true;
            setColumnVisibilityModelRef.current(parsed.columnVisibilityModel);
          }
          if (parsed.columnOrder) {
            setColumnOrderRef.current(parsed.columnOrder);
          }

          onSuccessRef.current(
            `${type === 'complete' ? 'Complete search' : 'Column configuration'} "${parsed.name}" applied successfully`,
          );

          removeUrlParam(type === 'complete' ? 'loadComplete' : 'loadColumns');
        } else {
          onErrorRef.current(`${type === 'complete' ? 'Complete search' : 'Column configuration'} not found`);
        }
      } catch (error) {
        console.error(`Error loading legacy ${type} configuration:`, error);
        onErrorRef.current(`Error loading ${type} configuration`);
      }
    };

    if (loadCompleteId) {
      if (loadCompleteId.startsWith('completeSearch_')) {
        loadFromLocalStorage(loadCompleteId, 'complete');
      } else {
        loadConfigurationFromDynamoDB(loadCompleteId, true);
      }
    } else if (loadColumnsId) {
      if (loadColumnsId.startsWith('columnSelection_')) {
        loadFromLocalStorage(loadColumnsId, 'columns');
      } else {
        loadConfigurationFromDynamoDB(loadColumnsId, false);
      }
    }
    // Only re-run when the URL search params change; all callbacks are stable via refs
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  return { isLoadingConfiguration };
}
